import { useMemo } from "react";

import { useFrozenWhileEmpty } from "@/shared/hooks";
import { useObrasCards } from "../../hooks/use-obras-cards";
import { OBRAS_SEM_STATUS } from "../../obras-lista.constants";
import type { ObrasCardsColumn } from "../obras-cards";
import { OBRAS_CARDS_COLUNAS } from "./obras-cards-view.constants";
import type { ObrasCardsViewProps, ObrasCardsViewViewModel } from "./types-obras-cards-view";

/**
 * Colunas a partir dos status da API, na ordem e com o rótulo da visão Obra; status que a visão não conhece vêm depois, na
 * ordem da API e com a descrição dela, e "Sem status" (HU 2521 RN02) fecha o quadro.
 */
function ordenarColunasObra(statusOptions: readonly ObrasCardsColumn[]): ObrasCardsColumn[] {
  const visao = OBRAS_CARDS_COLUNAS.obra;
  const posicao = (code: string) => {
    if (code === OBRAS_SEM_STATUS.code) return Number.MAX_SAFE_INTEGER;
    const index = visao.findIndex((column) => column.code === code);
    return index === -1 ? visao.length : index;
  };

  return statusOptions
    .map((option, index) => ({ option, index, ordem: posicao(option.code) }))
    .sort((a, b) => a.ordem - b.ordem || a.index - b.index)
    .map(({ option }) => ({
      code: option.code,
      label: visao.find((column) => column.code === option.code)?.label ?? option.label,
    }));
}

export function useObrasCardsView({
  search,
  filters,
  isMobile,
  statusOptions,
  statusState,
  statusError,
  onClearSearch,
  onClearFilters,
}: ObrasCardsViewProps): ObrasCardsViewViewModel {
  const columns = useMemo(
    () =>
      ordenarColunasObra(statusOptions).filter(
        (column) => filters.statusObra.length === 0 || filters.statusObra.includes(column.code),
      ),
    [statusOptions, filters.statusObra],
  );

  const { columns: state, loadMore } = useObrasCards({
    search,
    filters,
    columns: useMemo(() => columns.map((column) => column.code), [columns]),
  });

  const forbidden = Object.values(state).some((column) => column?.status === "forbidden");

  // Nenhuma coluna com obra: o mesmo estado vazio da Lista, no lugar das colunas "Nenhum item".
  const isEmpty =
    columns.length > 0 &&
    columns.every(
      (column) => state[column.code]?.status === "success" && !state[column.code]?.total,
    );

  const empty = useFrozenWhileEmpty(isEmpty, { search, filters });

  // "Sem status" só vira coluna quando há obras sem status (ou a consulta falhou).
  const visibleColumns = columns.filter((column) => {
    if (column.code !== OBRAS_SEM_STATUS.code) return true;
    const data = state[column.code];
    return data?.status === "error" || (data?.status === "success" && data.total > 0);
  });

  return {
    search: empty.search,
    filters: empty.filters,
    isMobile,
    statusState,
    statusError,
    onClearSearch,
    onClearFilters,
    state,
    loadMore,
    forbidden,
    isEmpty,
    visibleColumns,
  };
}
